import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Download, Search } from 'lucide-react';
import * as api from '../../api';
import { Btn, RNLLoader, Table } from '../UI';

const fmtDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? '—' : x.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Approved take-offs that still have BOQ lines to request. "Load" opens the New Request form filled in
// from the take-off; saving it there links the request and marks those quantities as transferred.
export default function MRLoadTakeoffs({ onLoad, onBack }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setRows(null); setError('');
    try {
      api.clearApiCache();
      setRows(await api.listTakeoffsAwaitingMR({ search }) || []);
    } catch (e) {
      setRows([]); setError(e.message || 'Could not load approved take-offs.');
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { key: 'takeoff_ref', label: 'Ref', nowrap: true, render: (v, r) => (
      <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#0b318b' }}>{v} <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{r.revision}</span></span>
    ) },
    { key: 'project_code', label: 'Project', render: (v, r) => <span title={r.project_name}>{v || '—'}</span> },
    { key: 'building_zone', label: 'Building / Zone', minWidth: 120 },
    { key: 'discipline_name', label: 'Discipline' },
    { key: 'systems', label: 'System', minWidth: 160 },
    { key: 'open_lines', label: 'Lines to Request', align: 'right' },
    { key: 'material_count', label: 'Materials', align: 'right' },
    { key: 'date_prepared', label: 'Prepared', nowrap: true, render: fmtDate },
    { key: 'id', label: '', render: (id) => (
      <Btn style={{ padding: '6px 14px', fontSize: 12, background: '#16a34a', border: '1px solid #16a34a', color: '#fff', boxShadow: 'none' }}
           onClick={() => onLoad(id)}>
        <Download size={13} /> Load
      </Btn>
    ) },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 700, fontSize: 20, color: 'var(--text-primary)' }}>Load Approved Take-Offs</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            Raise a Material Request from an approved take-off — its systems, BOQ lines, remaining quantities and materials fill
            the request form. Only lines not yet requested are loaded, so nothing is requested twice.
          </p>
        </div>
        <Btn variant="secondary" onClick={onBack}><ArrowLeft size={14} /> Back to Dashboard</Btn>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); setSearch(q.trim()); }} style={{ display: 'flex', gap: 8, marginBottom: 14, maxWidth: 560 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search take-off ref / project / building / system…"
                 style={{ width: '100%', padding: '10px 12px 10px 33px', borderRadius: 11, border: '1px solid var(--border-light)', background: 'var(--bg-base)', color: 'var(--text-primary)', fontSize: 13.5, fontFamily: 'var(--font)' }} />
        </div>
        <Btn type="submit" disabled={rows === null}><Search size={14} /> Search</Btn>
      </form>

      {error && <div style={{ padding: '10px 14px', borderRadius: 10, background: '#fee2e2', color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {rows === null ? <RNLLoader title="Loading take-offs..." subtitle="Finding approved take-offs to request" /> : (
        <Table cols={cols} rows={rows}
               emptyMsg={search ? 'No approved take-offs match this search.' : 'No approved take-offs are waiting — every approved line has been requested.'} />
      )}
    </div>
  );
}
